/**
 * @package   FlorenceEGI — EGI — Public Variant Selector (M-EGI-281 · M-EGI-371)
 * @author    Padmin D. Curtis (AI Partner OS3.0-Compliant) for Fabio Cherici
 * @version   1.1.0
 * @date      2026-07-02
 * @purpose   Isola Vanilla TS del selettore VERSIONI pubblico su egis.show, SOLO DISPLAY.
 *            Legge il JSON embeddato server-side (prezzi/disponibilita AUTORITATIVI),
 *            e al cambio di un <select>-asse aggiorna LIVE prezzo, disponibilita e
 *            anteprima certificato. Nessun fetch (dati gia nel server render).
 *            M-EGI-371 — il pannello "Tutti i dettagli dell'opera" SEGUE la versione:
 *            ogni combo porta il fisico RISOLTO (cascata versione→opera, server-side) e
 *            le fasce spedizione RISOLTE per zona (override variant→egi); al cambio il
 *            client aggiorna peso/dimensioni, costi per zona (righe senza costo →
 *            nascoste) e il costo della zona del compratore. Payload legacy senza
 *            physical/bands → pannello non toccato (retro-compat).
 *
 * @contract CONFINE FASE DISPLAY: il selettore e' INFORMATIVO. NON collega la variante
 *           scelta al sistema di prenotazione/checkout (price-purchase-section). Il wiring
 *           acquisto e' fase successiva (decisione CEO). Qui: zero mutazione di stato server.
 *
 * @security XSS-safe (CWE-79): la scrittura nel DOM passa SOLO per textContent, mai per
 *           markup grezzo. Il parsing del JSON e' difensivo (unknown + type guard) ai
 *           confini DOM.
 */

import { computeComboPrice, type PricingMode } from "../sale-config/versions-model";

/** Un valore di un asse (chiave stabile + label visibile + dimensioni). */
interface SelectorValue {
  readonly key: string;
  readonly label: string;
  readonly dimensions: string | null;
}

/** Un asse del selettore. */
interface SelectorAxis {
  readonly axisKey: string;
  readonly label: string;
  readonly values: readonly SelectorValue[];
}

/** M-EGI-371 — fisico RISOLTO di una versione (cascata versione→opera fatta server-side). */
interface ComboPhysical {
  readonly weight_g: number | null;
  readonly dimensions_mm: {
    readonly l: number | null;
    readonly w: number | null;
    readonly h: number | null;
  };
}

/** M-EGI-371 — fasce spedizione RISOLTE per zona (override variant→egi, server-side). */
type ComboBands = Readonly<Record<string, { readonly cost: number }>>;

/** Una combinazione materializzata (autorita server: prezzo, disponibilita, sold-out). */
interface SelectorCombo {
  readonly valueKeys: readonly string[];
  readonly price: number;
  readonly editionSize: number | null;
  readonly available: number | null;
  readonly soldOut: boolean;
  readonly label: string | null;
  /** M-EGI-371 — assenti nei payload legacy: il pannello dettagli non viene toccato. */
  readonly physical?: ComboPhysical;
  readonly bands?: ComboBands;
}

/** Stringhe i18n (gia tradotte server-side). */
interface SelectorI18n {
  readonly sold_out: string;
  readonly available: string;
  readonly of: string;
  readonly no_combo: string;
  readonly cert_copy: string;
  readonly print: string;
  readonly select_hint: string;
}

/** Payload completo embeddato nel data-attribute. */
interface SelectorData {
  readonly basePrice: number;
  readonly pricingMode: PricingMode;
  readonly scarcityMode: string;
  readonly perVariant: boolean;
  readonly printYear: string | null;
  readonly axes: readonly SelectorAxis[];
  readonly combos: readonly SelectorCombo[];
  readonly i18n: SelectorI18n;
  /** M-EGI-371 — zona di spedizione del compratore (dal suo indirizzo default), se nota. */
  readonly buyerZone?: string | null;
  /** M-EGI-371 — dettagli di RIPIEGO a livello OPERA (combo non materializzata). */
  readonly details?: {
    readonly physical?: ComboPhysical;
    readonly bands?: ComboBands;
  };
}

/** Type guard difensivo ai confini DOM: valida la forma minima del payload. */
function isSelectorData(value: unknown): value is SelectorData {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.basePrice === "number" &&
    Array.isArray(v.axes) &&
    Array.isArray(v.combos) &&
    typeof v.i18n === "object" &&
    v.i18n !== null
  );
}

/** Formattazione prezzo EUR locale-aware (MiCA: il prezzo opera e' in EUR, mai Egili↔EUR). */
function formatEur(amount: number): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

/**
 * M-EGI-371 — SOLO il numero it-IT a 2 decimali ("15,00"): il simbolo € sta nel template
 * ("€ {n}", stesso formato del render server number_format(2, ',', '.') del pannello).
 */
function formatEurNumber(amount: number): string {
  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Chiave canonica di una combinazione: chiavi ordinate join '|' (match indipendente dall'ordine). */
function canonicalKey(keys: readonly string[]): string {
  return [...keys].sort().join("|");
}

/**
 * Controller dell'isola. Mantiene la mappa combo-per-chiave e aggiorna le viste live.
 */
class VariantSelector {
  private readonly data: SelectorData;
  /** M-EGI-371 — la section del selettore: il pannello dettagli vive al suo interno. */
  private readonly root: HTMLElement;
  private readonly selects: HTMLSelectElement[];
  private readonly priceEl: HTMLElement | null;
  private readonly availabilityEl: HTMLElement | null;
  private readonly certificateEl: HTMLElement | null;
  private readonly reasonEl: HTMLElement | null;
  private readonly comboByKey: Map<string, SelectorCombo>;

  public constructor(root: HTMLElement, data: SelectorData) {
    this.data = data;
    this.root = root;
    this.selects = Array.from(root.querySelectorAll<HTMLSelectElement>("[data-variant-axis]"));
    this.priceEl = root.querySelector<HTMLElement>("[data-variant-price]");
    this.availabilityEl = root.querySelector<HTMLElement>("[data-variant-availability]");
    this.certificateEl = root.querySelector<HTMLElement>("[data-variant-certificate]");
    this.reasonEl = root.querySelector<HTMLElement>("[data-variant-reason]");

    this.comboByKey = new Map();
    for (const combo of data.combos) {
      this.comboByKey.set(canonicalKey(combo.valueKeys), combo);
    }

    this.disableUnreachableOptions();

    for (const select of this.selects) {
      select.addEventListener("change", () => this.refresh());
    }

    this.refresh();
  }

  /**
   * Disabilita, per ogni asse, i valori che non appartengono ad ALCUNA combinazione
   * materializzata (vincolo a cascata semplice). Il motivo e' testuale (suffisso label),
   * non solo colore (WCAG 1.4.1 A): l'option disabilitata mostra il motivo nel testo.
   */
  private disableUnreachableOptions(): void {
    const reachable = new Set<string>();
    for (const combo of this.data.combos) {
      for (const k of combo.valueKeys) reachable.add(k);
    }

    for (const select of this.selects) {
      for (const option of Array.from(select.options)) {
        if (!reachable.has(option.value)) {
          option.disabled = true;
          option.textContent = `${option.textContent ?? ""} — ${this.data.i18n.no_combo}`;
        }
      }
      // Se la selezione corrente e' disabilitata, sposta al primo abilitato.
      if (select.selectedOptions[0]?.disabled) {
        const firstEnabled = Array.from(select.options).find((o) => !o.disabled);
        if (firstEnabled) select.value = firstEnabled.value;
      }
    }
  }

  /** Chiavi-valore correntemente selezionate, in ordine asse. */
  private currentKeys(): string[] {
    return this.selects.map((s) => s.value);
  }

  /** Aggiorna prezzo, disponibilita, anteprima certificato e motivo. */
  private refresh(): void {
    const keys = this.currentKeys();
    const combo = this.comboByKey.get(canonicalKey(keys)) ?? null;

    if (combo === null) {
      // Combinazione non materializzata: prezzo derivato (formula single-source) come
      // ripiego informativo, disponibilita ignota -> motivo testuale.
      this.renderUnavailable(keys);
      return;
    }

    this.renderCombo(combo, keys);
  }

  /** Combinazione non disponibile: prezzo stimato + motivo. */
  private renderUnavailable(keys: string[]): void {
    // Prezzo stimato con la formula condivisa (ratio NON noto qui -> base). Onesto: stima.
    const estimate = computeComboPrice(this.data.basePrice, this.data.pricingMode, [], null);
    this.setText(this.priceEl, formatEur(estimate));
    this.setText(this.availabilityEl, "—");
    this.setText(this.certificateEl, this.buildCertPreview(keys, null, null));
    this.showReason(this.data.i18n.no_combo);
    // M-EGI-371 — combo non materializzata: dettagli di ripiego a livello OPERA.
    this.updateDetails(this.data.details?.physical, this.data.details?.bands);
  }

  /** Combinazione disponibile (dato server autoritativo). */
  private renderCombo(combo: SelectorCombo, keys: string[]): void {
    this.setText(this.priceEl, formatEur(combo.price));
    // M-EGI-371 — il pannello "Tutti i dettagli" segue la versione selezionata.
    this.updateDetails(combo.physical, combo.bands);

    if (combo.soldOut) {
      this.setText(this.availabilityEl, this.data.i18n.sold_out);
      this.showReason(this.data.i18n.sold_out);
    } else if (this.data.perVariant && combo.editionSize !== null && combo.available !== null) {
      // "Disponibili: N di M"
      this.setText(
        this.availabilityEl,
        `${this.data.i18n.available}: ${combo.available} ${this.data.i18n.of} ${combo.editionSize}`,
      );
      this.hideReason();
    } else {
      // master_pool o nessun limite: nessun conteggio per-variante.
      this.setText(this.availabilityEl, this.data.i18n.available);
      this.hideReason();
    }

    this.setText(this.certificateEl, this.buildCertPreview(keys, combo.editionSize, combo.available));
  }

  /**
   * Anteprima certificato: "70x100 · Baryta · n. _/_ · stampa 2026".
   * n./M solo se per_variant con edition_size. L'anno solo se presente.
   */
  private buildCertPreview(keys: string[], editionSize: number | null, available: number | null): string {
    // Label leggibili dei valori selezionati (dalla definizione assi).
    const parts: string[] = [];
    for (let i = 0; i < this.selects.length; i++) {
      const axis = this.data.axes[i];
      const value = axis?.values.find((v) => v.key === keys[i]);
      if (value) parts.push(value.label);
    }

    const segments: string[] = [...parts];

    if (this.data.perVariant && editionSize !== null) {
      const sold = available !== null ? editionSize - available : null;
      const nextNumber = sold !== null ? sold + 1 : null;
      const numberStr = nextNumber !== null ? String(nextNumber) : "_";
      segments.push(`${this.data.i18n.cert_copy} ${numberStr}/${editionSize}`);
    }

    if (this.data.printYear) {
      segments.push(`${this.data.i18n.print} ${this.data.printYear}`);
    }

    return segments.join(" · ");
  }

  /**
   * M-EGI-371 — aggiorna il pannello "Tutti i dettagli" (dentro la section del selettore)
   * con fisico e fasce della versione selezionata. `undefined` (payload legacy) → il
   * pannello resta come renderizzato dal server. Le righe usano coppie dt/dd marcate
   * [data-details-row="…"]: si mostrano/nascondono INSIEME. Solo textContent (XSS-safe).
   */
  private updateDetails(physical?: ComboPhysical, bands?: ComboBands): void {
    if (physical !== undefined) {
      // Peso (g): numero > 0 → mostra; altrimenti nascondi la coppia dt/dd.
      const weight = typeof physical.weight_g === "number" && physical.weight_g > 0
        ? physical.weight_g
        : null;
      const weightEl = this.root.querySelector<HTMLElement>("[data-details-weight]");
      if (weightEl) weightEl.textContent = weight !== null ? String(weight) : "—";
      this.toggleDetailRows("weight", weight !== null);

      // Dimensioni (l × w × h): almeno una misura valorizzata → mostra.
      const dims = [physical.dimensions_mm.l, physical.dimensions_mm.w, physical.dimensions_mm.h]
        .filter((v): v is number => typeof v === "number" && v > 0);
      const dimsEl = this.root.querySelector<HTMLElement>("[data-details-dims]");
      if (dimsEl) dimsEl.textContent = dims.length > 0 ? dims.join(" × ") : "—";
      this.toggleDetailRows("dims", dims.length > 0);
    }

    if (bands !== undefined) {
      let anyBand = false;
      for (const zone of ["region", "national", "eu", "extra_eu"]) {
        const row = this.root.querySelector<HTMLElement>(`[data-details-band="${zone}"]`);
        const cell = this.root.querySelector<HTMLElement>(`[data-details-band-cost="${zone}"]`);
        const cost = bands[zone]?.cost;
        const has = typeof cost === "number";
        if (cell) cell.textContent = has ? `€ ${formatEurNumber(cost)}` : "";
        row?.classList.toggle("hidden", !has);
        if (has) anyBand = true;
      }
      // Nessuna fascia per questa versione → tabella nascosta, "da concordare" visibile.
      this.root.querySelector<HTMLElement>("[data-details-bands]")?.classList.toggle("hidden", !anyBand);
      this.root.querySelector<HTMLElement>("[data-details-no-bands]")?.classList.toggle("hidden", anyBand);

      // Costo della zona del compratore (se nota): aggiorna o nascondi la riga.
      const buyerZone = this.data.buyerZone ?? null;
      const buyerCost = buyerZone !== null ? bands[buyerZone]?.cost : undefined;
      const buyerRow = this.root.querySelector<HTMLElement>("[data-details-buyer]");
      const buyerCostEl = this.root.querySelector<HTMLElement>("[data-details-buyer-cost]");
      const hasBuyer = typeof buyerCost === "number";
      if (buyerCostEl) buyerCostEl.textContent = hasBuyer ? `€ ${formatEurNumber(buyerCost)}` : "—";
      buyerRow?.classList.toggle("hidden", !hasBuyer);
    }
  }

  /** Mostra/nasconde INSIEME tutti gli elementi (dt+dd) di una riga dettagli marcata. */
  private toggleDetailRows(key: string, show: boolean): void {
    this.root
      .querySelectorAll<HTMLElement>(`[data-details-row="${key}"]`)
      .forEach((el) => el.classList.toggle("hidden", !show));
  }

  private showReason(text: string): void {
    if (this.reasonEl === null) return;
    this.reasonEl.textContent = text;
    this.reasonEl.classList.remove("hidden");
  }

  private hideReason(): void {
    if (this.reasonEl === null) return;
    this.reasonEl.textContent = "";
    this.reasonEl.classList.add("hidden");
  }

  /** Scrittura DOM sicura: solo textContent (XSS-safe, CWE-79). */
  private setText(el: HTMLElement | null, text: string): void {
    if (el === null) return;
    el.textContent = text;
  }
}

/**
 * Bootstrap: inizializza ogni selettore presente nella pagina (di norma uno).
 * M-EGI-371 — esportata (initVariantSelectors) per i test jsdom: il modulo si
 * auto-inizializza comunque al load (comportamento invariato in pagina).
 */
export function initVariantSelectors(): void {
  const roots = document.querySelectorAll<HTMLElement>("[data-variant-selector]");
  roots.forEach((root) => {
    const raw = root.getAttribute("data-variant-selector-data");
    if (raw === null) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return; // payload malformato -> degrada silenziosamente, markup statico resta leggibile
    }

    if (!isSelectorData(parsed)) return;
    new VariantSelector(root, parsed);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initVariantSelectors);
} else {
  initVariantSelectors();
}
